/**
 * B6 · NIVELES — cliente REST de las rutas de progresión.
 *
 * Va por API y no por PowerSync porque el alcance no se expresa en una regla de
 * sync plana (los borradores solo para quien administra) y porque el estado del
 * alumno es server-authoritative: si el cliente lo calculara, diría algo
 * distinto que el servidor en cuanto una de las dos implementaciones cambiara.
 * `docs/DATA_ACCESS_POLICY.md` §2.
 *
 * Mismo transporte cookie-BFF que el resto de clientes REST del repo.
 */
import type { PrerequisiteEdge, TrackState } from "@space/shared";
import { apiJson } from "@/lib/rest";

const BASE = "/api/academy";

/**
 * Flag del CAMINO VISUAL, en un solo sitio (nada de literales sueltos). Las
 * otras keys del frente (`academy.progression`, `.prerequisites`, `.placement`,
 * `.upsell`) las decide el SERVIDOR y llegan resueltas en la respuesta: no se
 * duplican aquí para que el cliente no pueda opinar distinto.
 */
export const PROGRESSION_PATH_FEATURE = "academy.progression.path";

/** Etapa tal y como la devuelve el diseño de la ruta (no el estado del alumno). */
export interface TrackStage {
  id: string;
  stageKey: string;
  position: number;
  title: string;
  anchorKind: string;
  anchorId?: string | null;
  unlockConfig: unknown;
  completionConfig: unknown;
  onComplete: unknown;
  authoredFields: unknown;
}

export interface Track {
  id: string;
  name: string;
  description?: string | null;
  programId?: string | null;
  scopeKey: string;
  config: Record<string, unknown>;
  status: string;
  stages: TrackStage[];
  edges: PrerequisiteEdge[];
}

export interface TracksResponse {
  tracks: Track[];
  /** El registry se auto-describe: la interfaz no lleva la lista a mano. */
  scopes: string[];
  rewards: string[];
  enabled: boolean;
}

/** Oferta del siguiente nivel. `quote` ausente = tarjeta sin bloque de precio. */
export interface PendingOffer {
  offer_id: string;
  track_id: string;
  from_stage_id: string;
  next_stage_id?: string | null;
  next_stage_title?: string | null;
  valid_until?: string | null;
  quote?: {
    list_amount_usd_minor: number;
    offer_amount_usd_minor: number;
    savings_usd_minor: number;
    savings_pct: number;
    secondary_amount_minor?: number | null;
    exchange_rate?: number | null;
    installments_replaced: number;
    checkout_path: string;
  } | null;
}

const orgBase = (orgId: string): string => `${BASE}/${encodeURIComponent(orgId)}/progression`;

export function fetchTracks(orgId: string, drafts = false): Promise<TracksResponse> {
  // `drafts` solo lo honra el servidor si además puedes gestionar la academia:
  // pedirlo desde el cliente no concede nada.
  const search = new URLSearchParams({ drafts: String(drafts) });
  return apiJson<TracksResponse>(`${orgBase(orgId)}/tracks?${search.toString()}`);
}

/** Estado del PROPIO alumno en una ruta: lo que dibuja el camino. */
export function fetchMyState(orgId: string, trackId: string): Promise<TrackState> {
  return apiJson<TrackState>(`${orgBase(orgId)}/tracks/${encodeURIComponent(trackId)}/my-state`);
}

export function fetchMyOffers(orgId: string): Promise<PendingOffer[]> {
  return apiJson<PendingOffer[]>(`${orgBase(orgId)}/offers`);
}

export function dismissOffer(orgId: string, offerId: string): Promise<{ ok: boolean }> {
  return apiJson<{ ok: boolean }>(
    `${orgBase(orgId)}/offers/${encodeURIComponent(offerId)}/dismiss`,
    { method: "POST" },
  );
}

/** Claves de query (invalidación coherente entre camino, oferta y admin). */
export const progressionQueryKeys = {
  tracks: (orgId: string | undefined, drafts: boolean) =>
    ["progression", "tracks", orgId ?? "none", drafts] as const,
  myState: (orgId: string | undefined, trackId: string | undefined) =>
    ["progression", "my-state", orgId ?? "none", trackId ?? "none"] as const,
  offers: (orgId: string | undefined) => ["progression", "offers", orgId ?? "none"] as const,
};
